import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {{id: number, options: string[], available: boolean, price: number}} LookVariant
 * @typedef {{id: number, handle: string, url: string, title: string, short: string, image: string, srcset: string,
 *   thumb: string, colorIndex: number, sizeIndex: number, variants: LookVariant[]}} LookProduct
 */

/**
 * Evie & Bear "Complete the look" on the product page: the second piece of the look with its own size and color
 * pickers, "+ Add" and "Add both to cart" (one request, opens the cart drawer), and "Swap in" for the alternatives.
 * The partner's size follows the size chosen for the main product until the shopper picks a partner size.
 * Adds go to the cart through the same Ajax route and CartLinesUpdateEvent as Horizon's product form, so the cart
 * bubble and drawer update themselves. Every piece added here carries the hidden line item property `_look`.
 */
class EbCompleteLook extends HTMLElement {
  #abort = new AbortController();
  /** @type {{moneyFormat: string, currency: string, partnerId: number, main: LookProduct, candidates: LookProduct[], text: Record<string, string>}} */
  #data = /** @type {any} */ ({});
  /** @type {LookProduct | null} */
  #partner = null;
  #size = '';
  #color = '';
  #sizePicked = false;
  #busy = false;

  connectedCallback() {
    try {
      this.#data = JSON.parse(this.querySelector('[data-look-json]')?.textContent || '{}');
    } catch {
      return;
    }
    if (!this.#data.candidates?.length) return;
    this.#partner = this.#data.candidates[0] ?? null;

    const { signal } = this.#abort;
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('change', (event) => this.#onChange(event), { signal });

    const product = this.closest('eb-product');
    product?.addEventListener('eb:variant-change', () => this.#onMainChange(), { signal });
    product?.querySelectorAll('[data-look-jump]').forEach((el) => el.addEventListener('click', () => this.#jumpHere(), { signal }));

    this.#selectDefaults();
    this.#render();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  /** The size and variant chosen for the main product, read from the existing option radios. */
  get #mainSelection() {
    const product = this.closest('eb-product');
    const selected = [...(product?.querySelectorAll('.eb-pdp__option') ?? [])].map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    });
    const main = this.#data.main;
    const variant = main.variants.find((v) => v.options.every((value, i) => value === selected[i])) ?? null;
    const size = main.sizeIndex >= 0 ? selected[main.sizeIndex] || '' : '';
    return { variant, size };
  }

  /** @param {LookProduct} product */
  #colors(product) {
    if (product.colorIndex < 0) return [];
    const colors = [];
    for (const variant of product.variants) {
      const color = variant.options[product.colorIndex];
      if (variant.available && color && !colors.includes(color)) colors.push(color);
    }
    return colors;
  }

  /** @param {LookProduct} product */
  #sizes(product) {
    if (product.sizeIndex < 0) return [];
    const sizes = [];
    for (const variant of product.variants) {
      const size = variant.options[product.sizeIndex];
      if (size && !sizes.includes(size)) sizes.push(size);
    }
    return sizes;
  }

  /**
   * @param {LookProduct} product
   * @param {string} color
   * @param {string} size
   */
  #variant(product, color, size) {
    return (
      product.variants.find(
        (v) =>
          (product.colorIndex < 0 || v.options[product.colorIndex] === color) &&
          (product.sizeIndex < 0 || v.options[product.sizeIndex] === size)
      ) ?? null
    );
  }

  /** First available color, and the main product's size when the partner has it in stock. */
  #selectDefaults() {
    const partner = this.#partner;
    if (!partner) return;
    this.#color = this.#colors(partner)[0] || '';
    this.#sizePicked = false;
    this.#matchMainSize();
  }

  #matchMainSize() {
    const partner = this.#partner;
    if (!partner) return;
    if (partner.sizeIndex < 0) {
      this.#size = '';
      return;
    }
    const { size } = this.#mainSelection;
    const variant = size ? this.#variant(partner, this.#color, size) : null;
    this.#size = variant?.available ? size : '';
  }

  #onMainChange() {
    if (!this.#sizePicked) this.#matchMainSize();
    this.#render();
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.#data.moneyFormat, this.#data.currency);
  }

  /**
   * @param {string} key
   * @param {Record<string, string>} [values]
   */
  #t(key, values = {}) {
    return (this.#data.text?.[key] || '').replace(/\[(\w+)\]/g, (_, name) => values[name] ?? '');
  }

  #render() {
    const partner = this.#partner;
    if (!partner) return;
    const main = this.#data.main;
    const partnerVariant = partner.sizeIndex < 0 && partner.colorIndex < 0
      ? partner.variants.find((v) => v.available) ?? null
      : this.#variant(partner, this.#color, this.#size);
    const needsSize = partner.sizeIndex >= 0 && !this.#size;
    const { variant: mainVariant, size: mainSize } = this.#mainSelection;
    const ready = Boolean(partnerVariant?.available) && !needsSize;

    // Partner row
    const img = this.querySelector('.eb-look__img');
    if (img instanceof HTMLImageElement && img.getAttribute('src') !== partner.image) {
      img.src = partner.image;
      img.srcset = partner.srcset;
    }
    this.querySelectorAll('[data-look-link]').forEach((link) => link.setAttribute('href', partner.url));
    const name = this.querySelector('[data-look-name]');
    if (name) name.textContent = partner.title;
    const shownVariant = partnerVariant ?? partner.variants.find((v) => v.available) ?? partner.variants[0];
    const price = this.querySelector('[data-look-price]');
    if (price && shownVariant) price.textContent = this.#money(shownVariant.price);

    // Sentence: written for the main partner, so hidden once another piece is swapped in
    const text = this.querySelector('[data-look-text]');
    const sentence = this.querySelector('[data-look-sentence]');
    const partnerText = text?.getAttribute('data-partner-text') || '';
    const showSentence = partner.id === this.#data.partnerId ? partnerText : '';
    if (sentence) sentence.textContent = showSentence;
    if (text instanceof HTMLElement) text.hidden = !showSentence && !this.dataset.discount;

    // Color select: only when there is a choice
    const colors = this.#colors(partner);
    const colorField = this.querySelector('[data-look-color-field]');
    const colorSelect = this.querySelector('[data-look-color]');
    if (colorField instanceof HTMLElement) colorField.hidden = colors.length < 2;
    if (colorSelect instanceof HTMLSelectElement) {
      colorSelect.replaceChildren(...colors.map((color) => new Option(color, color, false, color === this.#color)));
    }

    // Size select: sold-out sizes disabled, "Choose size" while nothing fits
    const sizeSelect = this.querySelector('[data-look-size]');
    if (sizeSelect instanceof HTMLSelectElement) {
      const field = sizeSelect.closest('.eb-look__field');
      if (field instanceof HTMLElement) field.hidden = partner.sizeIndex < 0;
      const options = [];
      if (needsSize) options.push(new Option(this.#t('chooseSize'), '', true, true));
      for (const size of this.#sizes(partner)) {
        const option = new Option(size, size, false, size === this.#size);
        option.disabled = !this.#variant(partner, this.#color, size)?.available;
        options.push(option);
      }
      sizeSelect.replaceChildren(...options);
      sizeSelect.classList.toggle('is-needed', needsSize);
    }
    const match = this.querySelector('[data-look-match]');
    if (match instanceof HTMLElement) match.hidden = !this.#size || this.#sizePicked || this.#size !== mainSize;

    const add = this.querySelector('[data-look-add]');
    if (add instanceof HTMLButtonElement) {
      add.disabled = !ready || this.#busy;
      if (!add.classList.contains('is-done')) add.textContent = this.#t('add');
    }

    // "Add both to cart"
    const both = this.querySelector('[data-look-both]');
    if (both instanceof HTMLButtonElement) both.disabled = !ready || !mainVariant?.available || this.#busy;
    const bothThumb = this.querySelector('.eb-look__thumb-partner');
    if (bothThumb instanceof HTMLImageElement) bothThumb.src = partner.thumb;
    const jumpThumb = this.closest('eb-product')?.querySelector('.eb-look-jump__partner');
    if (jumpThumb instanceof HTMLImageElement) jumpThumb.src = partner.thumb;
    const detail = this.querySelector('[data-look-both-detail]');
    if (detail) {
      const mainPart = [main.short, mainSize].filter(Boolean).join(' ');
      // No size picked yet: "Jacket · choose size" instead of a bare question mark
      const partnerPart = partner.sizeIndex >= 0 && !this.#size
        ? `${partner.short} · ${this.#t('chooseSize').toLowerCase()}`
        : [partner.short, partner.sizeIndex >= 0 ? this.#size : ''].filter(Boolean).join(' ');
      detail.textContent = `${mainPart} + ${partnerPart}`;
    }
    const mainPrice = (mainVariant ?? main.variants.find((v) => v.available) ?? main.variants[0])?.price ?? 0;
    const partnerPrice = shownVariant?.price ?? 0;
    const total = mainPrice + partnerPrice;
    const percent = Number(this.dataset.discount) || 0;
    // Same as the cart: the discount is taken per item and rounded down to the cent
    const discounted = (cents) => cents - Math.floor((cents * percent) / 100);
    const totalEl = this.querySelector('[data-look-total]');
    const wasEl = this.querySelector('[data-look-total-was]');
    if (totalEl) totalEl.textContent = this.#money(percent ? discounted(mainPrice) + discounted(partnerPrice) : total);
    if (wasEl instanceof HTMLElement) {
      wasEl.hidden = !percent;
      wasEl.textContent = percent ? this.#money(total) : '';
    }
    const saveEl = this.querySelector('[data-look-save]');
    if (saveEl instanceof HTMLElement) {
      const saved = percent ? total - discounted(mainPrice) - discounted(partnerPrice) : 0;
      saveEl.hidden = saved <= 0;
      saveEl.textContent = saved > 0 ? this.#t('youSave', { amount: this.#money(saved) }) : '';
    }
    // Store credit on the look: same rounding as the Flow workflow (price paid × percent / 100, to whole cents)
    const creditEl = this.querySelector('[data-look-credit-amount]');
    const creditPercent = Number(this.querySelector('[data-look-credit]')?.getAttribute('data-credit-percent')) || 0;
    if (creditEl) {
      const paid = percent ? discounted(mainPrice) + discounted(partnerPrice) : total;
      creditEl.textContent = this.#money(Math.round((paid * creditPercent) / 100));
    }

    this.#renderAlternatives();
  }

  /** Cards for every candidate except the current partner (max. 12, a scrollable row), built from the embedded data. */
  #renderAlternatives() {
    const grid = this.querySelector('[data-look-alts]');
    const wrap = this.querySelector('[data-look-alts-wrap]');
    if (!grid) return;
    const others = this.#data.candidates.filter((c) => c.id !== this.#partner?.id).slice(0, 12);
    if (wrap instanceof HTMLElement) wrap.hidden = others.length === 0;
    grid.replaceChildren(
      ...others.map((product) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'eb-look__alt';
        button.dataset.lookSwap = String(product.id);
        const img = document.createElement('img');
        img.src = product.image;
        img.srcset = product.srcset;
        img.sizes = '(min-width: 990px) 140px, 30vw';
        img.alt = '';
        img.loading = 'lazy';
        img.width = 240;
        img.height = 300;
        const title = document.createElement('span');
        title.className = 'eb-look__alt-title';
        title.textContent = product.title;
        const price = document.createElement('span');
        price.className = 'eb-look__alt-price';
        const from = product.variants.find((v) => v.available) ?? product.variants[0];
        price.textContent = from ? this.#money(from.price) : '';
        const swap = document.createElement('span');
        swap.className = 'eb-look__alt-swap';
        swap.textContent = this.dataset.swapLabel || 'Swap in';
        button.append(img, title, price, swap);
        return button;
      })
    );
  }

  /** @param {Event} event */
  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (target.matches('[data-look-size]')) {
      this.#size = target.value;
      this.#sizePicked = Boolean(target.value);
    } else if (target.matches('[data-look-color]')) {
      this.#color = target.value;
      if (this.#size && !this.#variant(/** @type {LookProduct} */ (this.#partner), this.#color, this.#size)?.available) {
        this.#size = '';
        this.#sizePicked = false;
      }
    }
    this.querySelector('[data-look-add]')?.classList.remove('is-done');
    this.#render();
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const swap = target?.closest('[data-look-swap]');
    if (swap instanceof HTMLElement) {
      const next = this.#data.candidates.find((c) => String(c.id) === swap.dataset.lookSwap);
      if (next) {
        this.#partner = next;
        this.querySelector('[data-look-add]')?.classList.remove('is-done');
        this.#selectDefaults();
        this.#render();
        this.querySelector('[data-look-name]')?.closest('.eb-look__row')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      return;
    }
    if (target?.closest('[data-look-add]')) this.#add(false);
    else if (target?.closest('[data-look-both]')) this.#add(true);
  }

  /** Scroll to the block and briefly highlight it ("Shop the look" on the main photo). */
  #jumpHere() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    this.classList.add('is-flash');
    setTimeout(() => this.classList.remove('is-flash'), 1400);
    const focusTarget = this.querySelector('[data-look-size], [data-look-add]');
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
  }

  /** @param {boolean} both */
  async #add(both) {
    const partner = this.#partner;
    if (!partner || this.#busy) return;
    const partnerVariant = this.#variant(partner, this.#color, this.#size) ?? (partner.sizeIndex < 0 ? partner.variants.find((v) => v.available) : null);
    if (!partnerVariant?.available) return;
    const { variant: mainVariant, size: mainSize } = this.#mainSelection;
    if (both && !mainVariant?.available) return;

    const look = `${this.dataset.mainHandle}+${partner.handle}`;
    const items = [];
    // Both pieces carry _look (which pair) and _look_item: 'yes' (a fixed value the discount app can match on)
    const properties = { _look: look, _look_item: 'yes' };
    if (both && mainVariant) items.push({ id: mainVariant.id, quantity: 1, properties });
    items.push({ id: partnerVariant.id, quantity: 1, properties });

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    this.#busy = true;
    this.#render();
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      let cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (!data.status && !both) {
        // Partner added on its own: also mark the main product already in the cart, if it isn't part of a look yet
        const mainLine = cart.items.find((line) => line.product_id === this.#data.main.id && !line.properties?._look);
        if (mainLine) {
          const changed = await fetch(`${Theme.routes.cart_change_url}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: mainLine.key,
              quantity: mainLine.quantity,
              properties: { ...(mainLine.properties || {}), ...properties },
              sections: sectionIds.join(','),
            }),
          }).then((r) => r.json());
          if (!changed.status) {
            cart = changed;
            if (changed.sections) data.sections = changed.sections;
          }
        }
      }
      if (data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'eb-complete-look', itemCount: 0 },
        });
        this.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
        this.#announce(data.description || data.message || this.#t('error'));
        return;
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          sections: data.sections,
          source: 'eb-complete-look',
          itemCount: items.length,
        },
      });

      const add = this.querySelector('[data-look-add]');
      add?.classList.add('is-done');
      if (add) add.textContent = this.#t('added');
      const partnerLabel = [partner.short, this.#size].filter(Boolean).join(' ');
      if (both) {
        this.#announce(this.#t('bothAddedMessage', { main: [this.#data.main.short, mainSize].filter(Boolean).join(' '), partner: partnerLabel }));
        const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
        if (drawer && 'open' in drawer && typeof drawer.open === 'function' && !(/** @type {any} */ (drawer).isOpen)) drawer.open();
      } else {
        this.#announce(this.#t('addedMessage', { partner: partnerLabel }));
      }
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: String(error), code: 'SERVICE_UNAVAILABLE' }));
      this.#announce(this.#t('error'));
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  /** @param {string} message */
  #announce(message) {
    const live = this.querySelector('[data-look-live]');
    if (!live) return;
    live.textContent = '';
    requestAnimationFrame(() => (live.textContent = message));
  }
}

if (!customElements.get('eb-complete-look')) customElements.define('eb-complete-look', EbCompleteLook);
